/* eslint-disable @typescript-eslint/no-explicit-any */

import { FormEvent, useEffect, useState } from 'react';
import {
  Button,
  Container,
  DropdownField,
  ExportTableButton,
} from '@/components';

import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import { useAuthContext } from '@/hooks';
import { TableData } from '@/components/Table/types';
import { getArmadoresList } from '@/services/getListArmadores';

import { getStockContainers } from './getStockContainersList';
import { StockContainersInfo } from './types';

import StockContainersTable from './components/StockContainersTable/StockContainersTable';
import { columnsToExport } from './columnsToExport';
import styles from './StockContainers.module.scss';

const FILE_NAME = `StockContainers-Montecon`;

const StockExportContainers = () => {
  const { t } = useTranslation('stockContainers');
  const { userDataDecoded } = useAuthContext();

  const getStockContainersMutation = useMutation({
    mutationFn: getStockContainers,
  });

  const { data: assignListDropdown } = useQuery({
    queryKey: ['armadoresListDropdown'],
    queryFn: getArmadoresList,
  });

  const [selectedShipOwner, setSelectedShipOwner] = useState<string | null>(
    null
  );

  const shipownerOptions = assignListDropdown?.map((val: any) => {
    return {
      value: val.codigo,
      name: val.nombre,
    };
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedShipOwner) {
      getStockContainersMutation.mutate({
        armador: selectedShipOwner,
      });
    }
  };

  const submitButtonText = t('query');

  const isTableHidden =
    !getStockContainersMutation.data && !getStockContainersMutation.isPending;

  const handleDropdownChange = (e: { value: string | null }) => {
    setSelectedShipOwner(e.value);
  };

  const options = userDataDecoded?.armadores?.split(';').map((val) => {
    return {
      value: val,
      name: val,
    };
  });

  const [parsedData, setParsedData] = useState<StockContainersInfo[]>([]);

  useEffect(() => {
    if (getStockContainersMutation.data) {
      const body = Array.isArray(getStockContainersMutation.data)
        ? getStockContainersMutation.data
        : [];
      const newData = body?.map((item: any) => {
        const { ...rest } = item;

        const tipoTamano = item?.tipoTamano.match(/([A-Za-z]+)(\d{2})/);

        return {
          ...rest,
          tamano: tipoTamano[2],
          tipo: tipoTamano[1],
          inicioOps: item?.inicioOps ? new Date(item?.inicioOps) : '',
        };
      });
      setParsedData(newData);
    }
  }, [getStockContainersMutation.data]);

  const shipowner =
    userDataDecoded?.armadores === '*' ? shipownerOptions : options;

  return (
    <Container title={t('exportContainers')} className={styles.containerList}>
      <header>
        <form onSubmit={handleSubmit}>
          <DropdownField
            id="selectedShipOwner"
            options={shipowner}
            onChange={(e) => handleDropdownChange(e)}
            placeholder={t('selectShipowner')}
            label={t('shipowner')}
            value={selectedShipOwner}
            className={styles.dropdown}
            required
          />

          <Button
            type="submit"
            className={styles.submitButton}
            disabled={!selectedShipOwner}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.icon} />
            {submitButtonText}
          </Button>
        </form>
        {!isTableHidden && (
          <ExportTableButton
            data={getStockContainersMutation.data as TableData}
            isDisabled={getStockContainersMutation.isPending}
            columnsToExport={columnsToExport}
            fileName={FILE_NAME}
          />
        )}
      </header>
      {!isTableHidden && (
        <StockContainersTable
          data={parsedData}
          isLoading={getStockContainersMutation.isPending}
        />
      )}
    </Container>
  );
};

export default StockExportContainers;
